import { useState, useEffect, FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatusBadge from '../../components/StatusBadge';
import { clientApi } from '../../services/client';
import { authService } from '../../services/auth';
import { useAuthStore } from '../../stores/authStore';
import api from '../../services/api';
import extractApiError from '../../utils/extractApiError';
import type { TeamMember, InviteItem } from '../../types';

type SettingsTab = 'general' | 'team' | 'notifications' | 'security';
type TeamSubTab = 'members' | 'pending';

const inputClass =
  'w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

const NOTIFICATION_LABELS: Record<string, string> = {
  score_changes: 'Score Changes',
  upload_completions: 'Upload Completions',
  upload_failures: 'Upload Failures',
  site_invitations: 'Site Invitations',
  payment_alerts: 'Payment Alerts',
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString();
}

function formatLastActive(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

export default function ClientSettings() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const loadUser = useAuthStore((s) => s.loadUser);

  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [teamSubTab, setTeamSubTab] = useState<TeamSubTab>('members');

  // --- General tab state ---
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
  });
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const updateProfileMutation = useMutation({
    mutationFn: (data: { first_name: string; last_name: string; phone: string }) =>
      authService.updateMe(data),
    onSuccess: () => {
      loadUser();
      setProfileError(null);
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    },
    onError: (err: unknown) => {
      setProfileSuccess(false);
      setProfileError(extractApiError(err));
    },
  });

  const handleProfileSubmit = (e: FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileForm);
  };

  // --- Team tab ---
  const { data: teamData, isLoading: teamLoading } = useQuery({
    queryKey: ['client', 'team'],
    queryFn: () => clientApi.getTeam().then((r) => r.data),
  });

  const { data: invites, isLoading: invitesLoading } = useQuery({
    queryKey: ['client', 'invites'],
    queryFn: () => clientApi.getInvites().then((r) => r.data),
  });

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const createInviteMutation = useMutation({
    mutationFn: (data: { email: string; org_role: 'member' | 'admin' }) =>
      clientApi.createInvite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', 'invites'] });
      setInviteEmail('');
      setInviteRole('member');
      setInviteError(null);
      setInviteSuccess(true);
      setTimeout(() => setInviteSuccess(false), 3000);
    },
    onError: (err: unknown) => {
      setInviteError(extractApiError(err));
    },
  });

  const handleInviteSubmit = (e: FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSuccess(false);
    createInviteMutation.mutate({ email: inviteEmail, org_role: inviteRole });
  };

  const teamMembers: TeamMember[] = teamData?.results ?? [];
  const inviteList: InviteItem[] = invites ?? [];

  // --- Notifications tab ---
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>({
    score_changes: true,
    upload_completions: true,
    upload_failures: true,
    site_invitations: true,
    payment_alerts: true,
  });
  const [notifSuccess, setNotifSuccess] = useState(false);
  const [notifError, setNotifError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.notification_preferences) {
      setNotifPrefs((prev) => ({ ...prev, ...user.notification_preferences }));
    }
  }, [user]);

  const updateNotifMutation = useMutation({
    mutationFn: (prefs: Record<string, boolean>) =>
      api.patch('/auth/me/', { notification_preferences: prefs }),
    onSuccess: () => {
      loadUser();
      setNotifError(null);
      setNotifSuccess(true);
      setTimeout(() => setNotifSuccess(false), 3000);
    },
    onError: (err: unknown) => {
      setNotifSuccess(false);
      setNotifError(extractApiError(err));
    },
  });

  const handleNotifSubmit = (e: FormEvent) => {
    e.preventDefault();
    updateNotifMutation.mutate(notifPrefs);
  };

  // --- Security tab ---
  const [pwForm, setPwForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  const changePwMutation = useMutation({
    mutationFn: ({ old_password, new_password }: { old_password: string; new_password: string }) =>
      authService.changePassword(old_password, new_password),
    onSuccess: () => {
      setPwForm({ old_password: '', new_password: '', confirm_password: '' });
      setPwError(null);
      setPwSuccess(true);
      setTimeout(() => setPwSuccess(false), 3000);
    },
    onError: (err: unknown) => {
      setPwSuccess(false);
      setPwError(extractApiError(err));
    },
  });

  const handlePwSubmit = (e: FormEvent) => {
    e.preventDefault();
    setPwError(null);
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError('Passwords do not match.');
      return;
    }
    if (pwForm.new_password.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    changePwMutation.mutate({
      old_password: pwForm.old_password,
      new_password: pwForm.new_password,
    });
  };

  // --- Tabs ---
  const tabs: { key: SettingsTab; label: string }[] = [
    { key: 'general', label: 'General' },
    { key: 'team', label: 'Team' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'security', label: 'Security' },
  ];

  return (
    <>
      <PageHeader title="Settings" description="Manage your account, team, and preferences" />

      {/* Tab navigation */}
      <div className="mb-6 border-b border-slate-200">
        <nav className="-mb-px flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-b-2 border-brand-600 text-brand-600'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <form onSubmit={handleProfileSubmit} className="max-w-2xl">
          <div className="rounded-2xl border border-slate-100 bg-white p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Account Settings</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-slate-700 mb-1">
                  First Name
                </label>
                <input
                  id="first_name"
                  type="text"
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-slate-700 mb-1">
                  Last Name
                </label>
                <input
                  id="last_name"
                  type="text"
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="email_display" className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  id="email_display"
                  type="email"
                  value={user?.email ?? ''}
                  disabled
                  className={`${inputClass} bg-slate-50 text-slate-500 cursor-not-allowed`}
                />
                <p className="mt-1 text-xs text-slate-400">Email cannot be changed.</p>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">
                  Phone
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="submit"
                disabled={updateProfileMutation.isPending}
                className="px-6 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
              >
                {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              {profileSuccess && (
                <span className="text-sm text-emerald-600 font-medium">Saved successfully</span>
              )}
              {profileError && (
                <span className="text-sm text-red-600 font-medium">{profileError}</span>
              )}
            </div>
          </div>
        </form>
      )}

      {/* Team Tab */}
      {activeTab === 'team' && (
        <div className="max-w-4xl">
          {/* Sub-tabs */}
          <div className="mb-4 flex gap-4">
            <button
              onClick={() => setTeamSubTab('members')}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                teamSubTab === 'members'
                  ? 'bg-brand-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Members
            </button>
            <button
              onClick={() => setTeamSubTab('pending')}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                teamSubTab === 'pending'
                  ? 'bg-brand-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Pending{inviteList.length > 0 && ` (${inviteList.length})`}
            </button>
          </div>

          {/* Members sub-tab */}
          {teamSubTab === 'members' && (
            <div className="rounded-2xl border border-slate-100 bg-white p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Team Members ({teamMembers.length})
              </h2>
              {teamLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : teamMembers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="pb-3 text-left font-medium text-slate-500">Name</th>
                        <th className="pb-3 text-left font-medium text-slate-500">Email</th>
                        <th className="pb-3 text-left font-medium text-slate-500">Role</th>
                        <th className="pb-3 text-left font-medium text-slate-500">Join Date</th>
                        <th className="pb-3 text-left font-medium text-slate-500">Last Active</th>
                        <th className="pb-3 text-left font-medium text-slate-500">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {teamMembers.map((member: TeamMember) => (
                        <tr key={member.id} className="hover:bg-slate-50">
                          <td className="py-3 font-medium text-slate-900">
                            {member.first_name} {member.last_name}
                          </td>
                          <td className="py-3 text-slate-600">{member.email}</td>
                          <td className="py-3">
                            <StatusBadge
                              label={member.role}
                              variant={member.role === 'admin' ? 'info' : 'neutral'}
                            />
                          </td>
                          <td className="py-3 text-slate-600">{formatDate(member.created_at)}</td>
                          <td className="py-3 text-slate-600">{formatLastActive(member.last_login)}</td>
                          <td className="py-3">
                            <StatusBadge
                              label={member.is_active ? 'Active' : 'Inactive'}
                              variant={member.is_active ? 'success' : 'neutral'}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-400">No team members found.</p>
              )}
            </div>
          )}

          {/* Pending sub-tab */}
          {teamSubTab === 'pending' && (
            <div className="space-y-6">
              {/* Invite form */}
              <div className="rounded-2xl border border-slate-100 bg-white p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Invite Team Member</h2>
                <form onSubmit={handleInviteSubmit} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="email@example.com"
                    className={`flex-1 ${inputClass}`}
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as 'member' | 'admin')}
                    className={inputClass}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button
                    type="submit"
                    disabled={createInviteMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {createInviteMutation.isPending ? 'Sending...' : 'Send Invite'}
                  </button>
                </form>
                {inviteError && <p className="mt-2 text-sm text-red-600">{inviteError}</p>}
                {inviteSuccess && (
                  <p className="mt-2 text-sm text-emerald-600 font-medium">Invite sent successfully</p>
                )}
              </div>

              {/* Pending invites list */}
              <div className="rounded-2xl border border-slate-100 bg-white p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">
                  Pending Invites ({inviteList.length})
                </h2>
                {invitesLoading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : inviteList.length > 0 ? (
                  <div className="space-y-3">
                    {inviteList.map((invite: InviteItem) => (
                      <div
                        key={invite.id}
                        className="flex items-center justify-between p-3 rounded-xl bg-slate-50"
                      >
                        <div>
                          <p className="text-sm font-medium text-slate-900">{invite.email}</p>
                          <p className="text-xs text-slate-500">
                            Invited {new Date(invite.created_at).toLocaleDateString()}
                            {invite.invited_by_name && ` by ${invite.invited_by_name}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge label={invite.org_role} variant="neutral" />
                          <StatusBadge
                            label={invite.status}
                            variant={
                              invite.status === 'pending'
                                ? 'warning'
                                : invite.status === 'accepted'
                                ? 'success'
                                : 'error'
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No pending invites.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <form onSubmit={handleNotifSubmit} className="max-w-2xl">
          <div className="rounded-2xl border border-slate-100 bg-white p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Notification Preferences</h2>
            <p className="text-sm text-slate-500 mb-6">
              Choose which notifications you would like to receive.
            </p>
            <div className="space-y-4">
              {Object.entries(NOTIFICATION_LABELS).map(([key, label]) => (
                <label
                  key={key}
                  className="flex items-center justify-between p-3 rounded-xl bg-slate-50 cursor-pointer"
                >
                  <span className="text-sm font-medium text-slate-700">{label}</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={notifPrefs[key] ?? true}
                      onChange={(e) =>
                        setNotifPrefs({ ...notifPrefs, [key]: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-10 h-6 bg-slate-300 peer-checked:bg-brand-600 rounded-full transition-colors" />
                    <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="submit"
                disabled={updateNotifMutation.isPending}
                className="px-6 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
              >
                {updateNotifMutation.isPending ? 'Saving...' : 'Save Preferences'}
              </button>
              {notifSuccess && (
                <span className="text-sm text-emerald-600 font-medium">Saved successfully</span>
              )}
              {notifError && (
                <span className="text-sm text-red-600 font-medium">{notifError}</span>
              )}
            </div>
          </div>
        </form>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <form onSubmit={handlePwSubmit} className="max-w-2xl">
          <div className="rounded-2xl border border-slate-100 bg-white p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Change Password</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="old_password" className="block text-sm font-medium text-slate-700 mb-1">
                  Current Password
                </label>
                <input
                  id="old_password"
                  type="password"
                  required
                  value={pwForm.old_password}
                  onChange={(e) => setPwForm({ ...pwForm, old_password: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-slate-700 mb-1">
                  New Password
                </label>
                <input
                  id="new_password"
                  type="password"
                  required
                  value={pwForm.new_password}
                  onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  htmlFor="confirm_password"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Confirm New Password
                </label>
                <input
                  id="confirm_password"
                  type="password"
                  required
                  value={pwForm.confirm_password}
                  onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="submit"
                disabled={changePwMutation.isPending}
                className="px-6 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
              >
                {changePwMutation.isPending ? 'Changing...' : 'Change Password'}
              </button>
              {pwSuccess && (
                <span className="text-sm text-emerald-600 font-medium">
                  Password changed successfully
                </span>
              )}
              {pwError && <span className="text-sm text-red-600 font-medium">{pwError}</span>}
            </div>
          </div>
        </form>
      )}
    </>
  );
}
